/**
 * @fileoverview Candidacy Actions Section Component
 * 
 * Displays action buttons for candidacy management (Approve, Reject, Send Invite)
 */

import { useState } from "react";
import { Button } from "components/ui/button";
import { CalendarCheck } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { AlternateDateSelectionModal } from "components/modals/AlternateDateSelectionModal";

interface CandidacyActionsSectionProps {
  entity: any;
  onUpdate?: (updated: any) => void;
  onRefresh?: () => void;
}

export const CandidacyActionsSection: React.FC<CandidacyActionsSectionProps> = ({ 
  entity, 
  onRefresh 
}) => {
  const { session } = useSession();
  const [isDateSelectionModalOpen, setIsDateSelectionModalOpen] = useState(false);
  const [isSelectingDate, setIsSelectingDate] = useState(false);

  if (!entity) return null;


  const handleOpenDateSelection = () => {
    setIsDateSelectionModalOpen(true);
  };

  const handleDateSelectionConfirm = async (selectedDate: Date) => {
    if (!entity?.id) return;

    setIsSelectingDate(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/select-alternate-date/${entity.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selectedDate: selectedDate.toISOString()
          })
        },
        session
      );

      if (response.ok) {
        toast.success('Datum succesvol geselecteerd!');
        setIsDateSelectionModalOpen(false);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error('Fout bij selecteren van datum');
      }
    } catch (error) {
      console.error('Error selecting date:', error);
      toast.error('Fout bij selecteren van datum');
    } finally {
      setIsSelectingDate(false);
    }
  };

  const handleCloseDateSelection = () => {
    setIsDateSelectionModalOpen(false);
  };

  // Determine which actions to show based on status (only Date Selection here, Send Invite is in header)
  const showDateSelection = entity.status === 'awaiting-alternate-selection' && entity.alternateDates && entity.alternateDates.length > 0;

  // Don't show the section if no actions are available
  if (!showDateSelection) {
    return null;
  }

  return (
    <>
      <div className="bleed-full-width border-y border-neutral-primary/10 py-4 mb-6">
        <div className="flex flex-wrap items-center gap-3 justify-center">
          <Button
            onClick={handleOpenDateSelection}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            <CalendarCheck size={20} weight="duotone" className="mr-2" />
            Datum selecteren
          </Button>
        </div>
      </div>

      {/* Date Selection Modal */}
      {showDateSelection && (
        <AlternateDateSelectionModal
          isOpen={isDateSelectionModalOpen}
          onClose={handleCloseDateSelection}
          alternateDates={entity.alternateDates || []}
          onConfirm={handleDateSelectionConfirm}
          isLoading={isSelectingDate}
          candidateName={entity.personalDetails ? `${entity.personalDetails.firstName} ${entity.personalDetails.lastName}` : 'Kandidaat'}
        />
      )}
    </>
  );
};

